import React, { FC } from 'react';
import cx from 'classnames';
import { useCurrentLocale } from '../../utils/translations';
import { Menu as MenuType, MenuItem as MenuItemType } from '../../types';
import Menu from '../Menu';
import Link, { MaskedExternalLink } from '../Link';
import Action from '../Action';
import styles from './Navigation.css';
import getLocalizedUrl from '../../utils/urls';

type Props = {
  classNames?: {
    root?: string;
    action?: string;
    link?: string;
  };
  index: number;
  length: number;
  navigationItem: MenuType | MenuItemType;
  onClick?: (
    name: string,
    details?: { [key: string]: string | undefined },
    event?: Event
  ) => void;
};

const NavigationItem: FC<Props> = ({
  classNames,
  index,
  navigationItem,
  length,
  onClick
}): JSX.Element | null => {
  const locale = useCurrentLocale();
  const { label, name } = navigationItem;
  const { icon, items } = navigationItem as MenuType;

  // adjust position of last 2 menus (or last menu for small nav bars)
  const isRear = index === length - 1 || (length > 2 && index === length - 2);

  if (items !== undefined) {
    return (
      <Menu
        classNames={{
          root: cx(classNames?.root, styles.item),
          items: cx({
            [styles.rear]: isRear
          })
        }}
        dataTestId={navigationItem.testId}
        key={name}
        icon={icon}
        items={items}
        label={label}
        name={name}
        onClick={onClick}
      />
    );
  }

  const { crawlable, url, testId, type } = navigationItem as MenuItemType;
  const localizedUrl = getLocalizedUrl(url, locale);

  if (type === 'masked' && localizedUrl) {
    return (
      <MaskedExternalLink
        key={name}
        className={cx(classNames?.root, styles.item, classNames?.link)}
        icon={icon}
        label={label}
        dataTestId={testId}
        name={name}
        url={localizedUrl}
        onClick={onClick}
      />
    );
  }

  if (type === 'link' || type === 'route') {
    return (
      // eslint-disable-next-line jsx-a11y/anchor-is-valid
      <Link
        key={name}
        className={cx(
          classNames?.root,
          styles.item,
          styles.link,
          classNames?.link
        )}
        dataTestId={testId}
        icon={icon}
        isMasked={crawlable === false}
        label={label}
        name={name}
        type={type}
        url={localizedUrl}
        onClick={onClick}
      />
    );
  }

  if (type === 'action') {
    return (
      <Action
        key={name}
        className={cx(classNames?.root, styles.item, classNames?.action, {
          [styles.lastAction]: isRear
        })}
        dataTestId={testId}
        icon={icon}
        label={label}
        name={name}
        onClick={onClick}
      />
    );
  }

  return null;
};

export default NavigationItem;
